import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Link2, Unlink } from 'lucide-react';
import { Member, GustoUser, GustoUserSummary } from '../../types';
import {
  useUnassignedGustoUsers,
  useAssignGustoUser,
  useUnassignGustoUser,
} from '../../hooks';

interface GustoUserAssignmentProps {
  person: Member;
}

export const GustoUserAssignment: React.FC<GustoUserAssignmentProps> = ({ person }) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation('common');

  const { data: unassignedUsers = [], isLoading: loadingUsers } = useUnassignedGustoUsers();
  const assignMutation = useAssignGustoUser();
  const unassignMutation = useUnassignGustoUser();

  const [selectedGustoUserId, setSelectedGustoUserId] = useState<string>('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasGustoUser = !!person.gustoUser;

  const selectedGustoUser = unassignedUsers.find((u: GustoUser) => u.id === selectedGustoUserId);

  const gustoUserLabel = (user: GustoUser | GustoUserSummary): string => {
    const parts: string[] = [];
    if (user.name) parts.push(user.name);
    if (user.email) parts.push(`(${user.email})`);
    if (!user.name && !user.email && 'gustoUuid' in user) parts.push(user.gustoUuid);
    return parts.join(' ');
  };

  const assignedGustoUserLabel = person.gustoUser
    ? gustoUserLabel(person.gustoUser) || person.gustoUser.id
    : '';

  const handleAssignClick = () => {
    if (!selectedGustoUserId) return;
    setError(null);
    setAssignDialogOpen(true);
  };

  const handleConfirmAssign = async () => {
    try {
      await assignMutation.mutateAsync({
        gustoUserId: selectedGustoUserId,
        memberId: person.id,
      });
      setSelectedGustoUserId('');
      setAssignDialogOpen(false);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : tCommon('error.generic');
      setError(message);
      setAssignDialogOpen(false);
    }
  };

  const handleUnassignClick = () => {
    setError(null);
    setUnassignDialogOpen(true);
  };

  const handleConfirmUnassign = async () => {
    if (!person.gustoUser) return;
    try {
      await unassignMutation.mutateAsync(person.gustoUser.id);
      setUnassignDialogOpen(false);
      setError(null);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : tCommon('error.generic');
      setError(message);
      setUnassignDialogOpen(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="gusto-user-assignment">
      {error && (
        <Alert variant="destructive" data-testid="gusto-assignment-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {hasGustoUser ? (
        <div className="flex items-center gap-2" data-testid="gusto-user-assigned">
          <Input
            value={assignedGustoUserLabel}
            disabled
            readOnly
            className="flex-1"
            data-testid="gusto-user-locked-field"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleUnassignClick}
            disabled={unassignMutation.isPending}
            data-testid="gusto-unassign-button"
          >
            <Unlink className="h-4 w-4 mr-1" />
            {t('form.gustoUser.unassign')}
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2" data-testid="gusto-user-unassigned">
          <Select
            value={selectedGustoUserId}
            onValueChange={setSelectedGustoUserId}
            disabled={loadingUsers}
          >
            <SelectTrigger data-testid="gusto-user-select" className="flex-1">
              <SelectValue placeholder={
                loadingUsers
                  ? tCommon('loading')
                  : t('form.gustoUser.selectPlaceholder')
              } />
            </SelectTrigger>
            <SelectContent>
              {unassignedUsers.map((user: GustoUser) => (
                <SelectItem key={user.id} value={user.id}>
                  {gustoUserLabel(user)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={handleAssignClick}
            disabled={!selectedGustoUserId || assignMutation.isPending}
            data-testid="gusto-assign-button"
          >
            <Link2 className="h-4 w-4 mr-1" />
            {t('form.gustoUser.assign')}
          </Button>
        </div>
      )}

      {/* Assign Confirmation Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent
          className="w-full max-w-md min-w-[min(90vw,20rem)]"
          data-testid="gusto-assign-dialog"
        >
          <DialogHeader className="min-w-0">
            <DialogTitle>{t('form.gustoUser.assignConfirmTitle')}</DialogTitle>
            <DialogDescription className="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]">
              {t('form.gustoUser.assignConfirmBody', {
                gustoUserName: selectedGustoUser ? gustoUserLabel(selectedGustoUser) : '',
                memberName: person.name,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setAssignDialogOpen(false)}
              disabled={assignMutation.isPending}
            >
              {tCommon('buttons.cancel')}
            </Button>
            <Button
              onClick={handleConfirmAssign}
              disabled={assignMutation.isPending}
              data-testid="gusto-assign-confirm-button"
            >
              {assignMutation.isPending ? tCommon('loading') : tCommon('buttons.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unassign Confirmation Dialog */}
      <Dialog open={unassignDialogOpen} onOpenChange={setUnassignDialogOpen}>
        <DialogContent
          className="w-full max-w-md min-w-[min(90vw,20rem)]"
          data-testid="gusto-unassign-dialog"
        >
          <DialogHeader className="min-w-0">
            <DialogTitle>{t('form.gustoUser.unassignConfirmTitle')}</DialogTitle>
            <DialogDescription className="min-w-0 whitespace-normal break-words [overflow-wrap:anywhere]">
              {t('form.gustoUser.unassignConfirmBody', {
                memberName: person.name,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => setUnassignDialogOpen(false)}
              disabled={unassignMutation.isPending}
            >
              {tCommon('buttons.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmUnassign}
              disabled={unassignMutation.isPending}
              data-testid="gusto-unassign-confirm-button"
            >
              {unassignMutation.isPending ? tCommon('loading') : tCommon('buttons.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
